import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import escape from 'lodash/escape';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfinity, faSliders } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Popover,
  Table,
} from 'react-bootstrap';
import { get, put } from '../../../helpers/fetch';
import { ReactSwal } from '../../../helpers/swal';
import { buildURL } from '../../../helpers/urls';
import RoundButton from '../../../components/RoundButton';
import style from './style.module.scss';

const formatDeploymentPretty = (deployment) => {
  if (deployment == null || deployment === '') {
    return '(no deployment in response)';
  }
  if (typeof deployment === 'string') {
    try {
      return JSON.stringify(JSON.parse(deployment), null, 2);
    } catch {
      return deployment;
    }
  }
  if (typeof deployment === 'object') {
    return JSON.stringify(deployment, null, 2);
  }
  return String(deployment);
};

const API_PATH = buildURL('/events/aiEnhance/runtimeConfig');

/** Empty placeholders only — never mistaken for server data until `configReady` */
const DEFAULT_FORM = {
  platform: '',
  enabled: false,
  min: '',
  max: '',
  t1Min: '',
  t1Max: '',
  t1Value: '',
  t2Value: '',
};

const AIEnhanceConfigs = ({ buttonClassName }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [configReady, setConfigReady] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await get(API_PATH);
      const config = { ...response?.config };
      delete config.t2Max;
      delete config.t2Min;
      setForm((prev) => {
        const merged = {
          ...prev,
          ...config,
          platform: response?.platform ?? prev.platform,
          enabled: response?.enabled ?? prev.enabled,
          max: response?.deployment?.workersMax,
        };
        const t1MinNum = Number(merged.t1Min);
        merged.t1Min = Number.isFinite(t1MinNum) && t1MinNum >= 1 ? t1MinNum : 1;
        return merged;
      });
      setConfigReady(true);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch AI enhance config');
      setConfigReady(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const onChange = (key, isBool = false) => (event) => {
    const value = isBool ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const onSave = async () => {
    if (!configReady) {
      return;
    }
    setSaving(true);
    setError('');
    const t1MinNum = Number(form.t1Min);
    if (!Number.isFinite(t1MinNum) || t1MinNum < 1) {
      setError('Tier 1 from (min) must be at least 1');
      setSaving(false);
      return;
    }
    try {
      const response = await put(API_PATH, {
        platform: form.platform,
        enabled: form.enabled,
        min: Number(form.min),
        max: Number(form.max),
        t1Min: t1MinNum,
        t1Max: Number(form.t1Max),
        t1Value: Number(form.t1Value),
        t2Value: Number(form.t2Value),
      });
      await loadConfig();
      await ReactSwal.fire({
        type: 'success',
        title: 'AI enhance config updated',
        html: `<pre class="text-left mb-0" style="max-height:50vh;overflow:auto;font-size:12px;">${escape(formatDeploymentPretty(response?.deployment))}</pre>`,
        width: 'min(900px, 95vw)',
        confirmButtonText: 'OK',
      });
    } catch (e) {
      console.error(e);
      setError('Failed to update AI enhance config');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    setForm(DEFAULT_FORM);
    setConfigReady(false);
    setError('');
    setShowModal(true);
    loadConfig();
  };

  const formLocked = !configReady || loading || saving;

  return (
    <>
      <OverlayTrigger
        placement="top"
        overlay={(
          <Popover id="aiEnhanceConfigs">
            <Popover.Content>
              Configure AI Enhance runtime values
            </Popover.Content>
          </Popover>
        )}
      >
        <span className="mr-2">
          <RoundButton className={buttonClassName} onClick={handleOpenModal}>
            <FontAwesomeIcon size="lg" icon={faSliders} className="align-middle" />
          </RoundButton>
        </span>
      </OverlayTrigger>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>AI Enhance Configs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="text-danger mb-2">{error}</div>}

          {loading && !configReady && (
            <p className="text-muted small mb-3">Loading configuration from the server…</p>
          )}
          {configReady && (
            <p className="text-muted small mb-3">Values below are loaded from the server.</p>
          )}
          {!configReady && !loading && error && (
            <p className="text-muted small mb-3">No configuration loaded — fix the error above or try again.</p>
          )}

          <div className="d-flex justify-content-between align-items-center mb-3">
            <span>Platform</span>
            <Form.Control
              as="select"
              className="w-auto"
              style={{ minWidth: 140 }}
              value={form.platform ?? ''}
              onChange={onChange('platform')}
              disabled={formLocked}
            >
              <option value="hybrid">Hybrid</option>
              <option value="runpod">Runpod</option>
              <option value="replicate">Replicate</option>
            </Form.Control>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <span>Enabled</span>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="switcher mr-0">
              <input
                type="checkbox"
                checked={!!form.enabled}
                className="switcher-input"
                onChange={onChange('enabled', true)}
                disabled={formLocked}
              />
              <span className="switcher-indicator">
                <span className="switcher-yes" />
                <span className="switcher-no" />
              </span>
            </label>
          </div>

          <div className="d-flex align-items-end mb-2">
            <Form.Group className="mr-2 mb-0">
              <Form.Label className="mb-1">Min Instances</Form.Label>
              <Form.Control
                className={style.numberInputNoSpin}
                type="number"
                value={form.min ?? ''}
                onChange={onChange('min')}
                disabled={formLocked}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label className="mb-1">Max Instances</Form.Label>
              <Form.Control
                className={style.numberInputNoSpin}
                type="number"
                value={form.max ?? ''}
                onChange={onChange('max')}
                disabled
              />
            </Form.Group>
          </div>

          <div className="mt-4 mb-2"><strong>Queue Tiers</strong></div>
          <Table responsive size="sm" borderless className="mb-3">
            <thead>
              <tr>
                <th className="border-0 pb-1 small text-muted font-weight-normal text-center">from</th>
                <th className="border-0 pb-1 small text-muted font-weight-normal text-center">to</th>
                <th className="border-0 pb-1 small text-muted font-weight-normal text-center px-1" />
                <th className="border-0 pb-1 small text-muted font-weight-normal text-center">instances</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="align-middle pt-0">
                  <Form.Control
                    className={classnames('text-center', style.numberInputNoSpin)}
                    style={{ minWidth: 52, maxWidth: 72 }}
                    type="number"
                    value={form.t1Min ?? ''}
                    min={1}
                    step={1}
                    onChange={onChange('t1Min')}
                    disabled={formLocked}
                  />
                </td>
                <td className="align-middle pt-0">
                  <Form.Control
                    className={classnames('text-center', style.numberInputNoSpin)}
                    style={{ minWidth: 52, maxWidth: 72 }}
                    type="number"
                    value={form.t1Max ?? ''}
                    onChange={onChange('t1Max')}
                    disabled={formLocked}
                  />
                </td>
                <td className="align-middle text-center text-muted pt-0 px-1">=</td>
                <td className="align-middle pt-0">
                  <Form.Control
                    className={classnames('text-center', style.numberInputNoSpin)}
                    style={{ minWidth: 52, maxWidth: 72 }}
                    type="number"
                    value={form.t1Value ?? ''}
                    onChange={onChange('t1Value')}
                    disabled={formLocked}
                  />
                </td>
              </tr>
              <tr>
                <th className="border-0 pb-1 pt-3 small text-muted font-weight-normal text-center">from</th>
                <th className="border-0 pb-1 pt-3 small text-muted font-weight-normal text-center">to</th>
                <th className="border-0 pb-1 pt-3 small text-muted font-weight-normal text-center px-1" />
                <th className="border-0 pb-1 pt-3 small text-muted font-weight-normal text-center">instances</th>
              </tr>
              <tr>
                <td className="align-middle pt-0">
                  <Form.Control
                    className={classnames('text-center', style.numberInputNoSpin)}
                    style={{ minWidth: 52, maxWidth: 72 }}
                    type="number"
                    value={form.t1Max ?? ''}
                    disabled
                    tabIndex={-1}
                    aria-label="from (same as tier 1 to)"
                  />
                </td>
                <td className="align-middle text-center text-secondary pt-0">
                  <FontAwesomeIcon icon={faInfinity} size="lg" title="Infinity" aria-label="Infinity" />
                </td>
                <td className="align-middle text-center text-muted pt-0 px-1">=</td>
                <td className="align-middle pt-0">
                  <Form.Control
                    className={classnames('text-center', style.numberInputNoSpin)}
                    style={{ minWidth: 52, maxWidth: 72 }}
                    type="number"
                    value={form.t2Value ?? ''}
                    onChange={onChange('t2Value')}
                    disabled={formLocked}
                  />
                </td>
              </tr>
            </tbody>
          </Table>

          <div className="text-center" style={{ marginBottom: 20 }}>
            <Button size="sm" onClick={onSave} disabled={formLocked}>
              {saving ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AIEnhanceConfigs;
